import { UrlObject } from "url";
import axios, { AxiosRequestConfig, Method, ResponseType } from "axios";
import Cookies from "js-cookie";
import { message } from "antd";

export interface RequestError {
  errorMessage?: string;
  error_description?: string;
}

/* Common Header */
axios.interceptors.request
  .use((request: any) => {
    const token = Cookies.get('token');
    if (token) {
      request.headers.Authorization = `Bearer ${token}`;
      // request.headers['Access-Control-Allow-Origin'] = '*';
    }
    return request;
  },
    (error) => {
      if (error.response && error.response.status === 401) {
        // router.push('/'); // Adjust the route as needed

        // Throw an exception to stop further execution
        return Promise.reject('Unauthorized');
      }
      // Handle other errors here
      return Promise.reject(error);
    }
  );

export const MethodConst = {
  DELETE: "DELETE",
  GET: "GET",
  POST: "POST",
  PUT: "PUT",
  PATCH: "PATCH",
} as const;

/**
 * Isomorphic fetch function
 * @param method restricted to GET', 'POST', 'PUT' and 'DELETE'
 * @param url Punycode only (not all Unicode characters)
 * @param data type `T` value
 * - string, plain object, ArrayBuffer, ArrayBufferView, URLSearchParams
 * - Browser only: FormData, File, Blob
 * - Node only: Stream, Buffer
 * @param responseType defaults to JSON
 * 'arraybuffer', 'document', 'json', 'text', 'stream'
 * - Browser only: 'blob'
 * @param withCredentials
 * @param headers e.g. {'authorization': 'Bearer TOKEN'}
 * @param params e.g. { ID: 12345 }
 * @returns desired response type `R` or `null` (204 No Content), otherwise `RequestError` object
 */
export const fetchWrapper = async <T, R>(
  Methods: Method,
  Url: UrlObject | string,
  Data: T | undefined,
  ResponseTypes: ResponseType,
  WithCredentials: boolean,
  Params?: Object | URLSearchParams,
): Promise<R | RequestError> => {
  const config: AxiosRequestConfig = {
    method: Methods,
    url: Url.toString(), // ! if Unicode support needed, then url.format() is required
    data: Methods === MethodConst.GET ? undefined : Data,
    responseType: ResponseTypes,
    withCredentials: WithCredentials,
    params: Params
  };
  // console.log("inside fetch wrapper", config);
  try {

    const response = await axios.request(config);
    switch (response.status) {
      case 200:
        try {
          const result = response.data;
          return result as R; // isAuthError/RequestError = false
        } catch (e: any) {
          return {
            errorMessage: e.message,
            error_description:
              "Something went wrong when parsing Response object",
          } as unknown as R;
        }
      case 201:
        try {
          const result = await response.data;
          return result as R; // isAuthError/RequestError = false
        } catch (e: any) {
          return {
            errorMessage: e.message,
            error_description:
              "Something went wrong when parsing Response object",
          } as unknown as R;
        }
      case 204:
        return new Promise((resolve, reject) => {
          resolve({
            errorMessage: undefined,
            error_description: undefined,
          } as unknown as R); // isAuthError/RequestError = false
        });
      case 400:
        try {
          const result = await response.data;
          return {
            errorMessage: !!response && response.statusText,
            error_description: "Error 400 from API server",
            ...result, // OBS.: overwrite fields
          } as unknown as R; // isAuthError/RequestError = true
        } catch (e: any) {
          return {
            errorMessage: e.message,
            error_description:
              "Error 400 from API server, without Response object",
          } as unknown as R; // isAuthError/RequestError = true
        }

      case 401:
        try {
          const result = await response.data;

          return {
            errorMessage: !!response && response.statusText,
            error_description: "Error 401 from API server",
            ...result, // OBS.: overwrite fields
          } as unknown as R; // isAuthError/RequestError = true
        } catch (e: any) {
          return {
            errorMessage: e.message,
            error_description:
              "Error 400 from API server, without Response object",
          } as unknown as R; // isAuthError/RequestError = true
        }
      default:
        return new Promise((resolve, reject) => {
          resolve({
            errorMessage: "Some error ocurred",
            error_description: `Error ${response.status} from API server`,
          } as unknown as R); // isAuthError/RequestError = true
        });

    }
  } catch (error: any) {
    const response = await error.response;
    switch (response?.status) {
      case 401:
        try {
          const result = await response.data;

          if (result?.msg === "Token Expired") {

            message.open({
              type: 'error',
              content: "Session expired. Please re-login!",
              key: 'global-error',
            });
          }

          setTimeout(() => {
            window.location.href = '/';
          }, 1000);

          return {
            errorMessage: !!response && response.statusText,
            error_description: "Error 401 from API server",
            ...result, // OBS.: overwrite fields
          } as unknown as R; // isAuthError/RequestError = true
        } catch (e: any) {
          return {
            errorMessage: e.message,
            error_description:
              "Error 401 from API server, without Response object",
          } as unknown as R; // isAuthError/RequestError = true
        }
      default:
        return Promise.reject(error);
    }
  }
};

export const removeDuplicatesByValue = (arr: any[], key?: string) => {
  const seen = new Set();
  return arr.filter(item => {
    const value = key ? item[`${key}`] : item.value
    if (seen.has(value)) {
      return false;
    }
    seen.add(value);
    return true;
  });
};

export const cleanData = (obj: any) => {
    for (var propName in obj) {
        if (
            obj[propName] === null ||
            obj[propName] === undefined ||
            obj[propName]?.length == 0
        ) {
            delete obj[propName];
        }
    }
    return obj;
};